/**
 * Database Inspector — Admin Portal
 *
 * Purpose-bound, time-limited, scoped read-only database access.
 * Requires super_admin role. Every session and query is audited.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { storage } from '../../services/storage';
import { API_BASE_URL } from '../../utils/api';

// Environment-aware API base — matches DashboardOverview, SyncHealthTab pattern
const API_BASE = window.location.hostname === 'localhost'
  ? '/api/admin'
  : `${API_BASE_URL}/api/admin`;

// Auth helper for admin endpoints
const adminFetch = async (url: string, options?: RequestInit): Promise<Response> => {
  const adminToken = await storage.getItem('admin_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options?.headers as Record<string, string>) || {}),
  };
  if (adminToken) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }
  return fetch(`${API_BASE}${url}`, { ...options, headers });
};

// Handle auth failures — same pattern as ComprehensiveAdminPortal
const handleAuthFailure = async (res: Response): Promise<boolean> => {
  if (res.status === 401 || res.status === 403) {
    await storage.removeItem('admin_token');
    window.location.href = '/admin/login';
    return true;
  }
  return false;
};

interface ScopeInfo {
  tables: string[];
  table_count: number;
  requires_target_user: boolean;
}

interface SessionInfo {
  session_id: string;
  reason: string;
  scopes: string[];
  target_user_id: string | null;
  created_at: string;
  expires_at: string;
  duration_minutes: number;
  query_count: number;
  status: 'active' | 'expired' | 'closed';
  tables_accessible: number;
}

interface QueryResult {
  success: boolean;
  message: string;
  data: Record<string, any>[];
  columns: string[];
  row_count: number;
  query_time_ms: number;
  session_expires_at: string;
}

// Scope display config
const SCOPE_META: Record<string, { label: string; color: string; icon: string }> = {
  user_health: { label: 'Health & Medical', color: '#ef4444', icon: '❤️' },
  user_training: { label: 'Training & Goals', color: '#f59e0b', icon: '🏋️' },
  user_sync: { label: 'Device Sync', color: '#3b82f6', icon: '🔄' },
  user_chat: { label: 'Chat & Messages', color: '#8b5cf6', icon: '💬' },
  user_memory: { label: 'Memory & Patterns', color: '#ec4899', icon: '🧠' },
  user_coaching: { label: 'Coaching Intelligence', color: '#14b8a6', icon: '🎯' },
  user_profile: { label: 'Profile & Preferences', color: '#6366f1', icon: '👤' },
  user_notifications: { label: 'Notifications', color: '#f97316', icon: '🔔' },
  user_files: { label: 'Files & Uploads', color: '#84cc16', icon: '📁' },
  user_subscription: { label: 'Subscription & Feedback', color: '#06b6d4', icon: '💳' },
  platform_research: { label: 'Research Library', color: '#1F6C4F', icon: '📚' },
  platform_system: { label: 'System Config', color: '#64748b', icon: '⚙️' },
  platform_ops: { label: 'Operations & Logs', color: '#78716c', icon: '📊' },
  platform_llm_intelligence: { label: 'LLM Intelligence', color: '#a855f7', icon: '🤖' },
  platform_campaigns: { label: 'Campaigns', color: '#e11d48', icon: '📢' },
  platform_content: { label: 'Content & Knowledge', color: '#0ea5e9', icon: '📝' },
};

export const DatabaseInspector: React.FC = () => {
  // State: flow stages
  const [stage, setStage] = useState<'setup' | 'query'>('setup');

  // Setup state
  const [scopes, setScopes] = useState<Record<string, ScopeInfo>>({});
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [duration, setDuration] = useState(30);
  const [scopeSearch, setScopeSearch] = useState('');
  const [expandedScope, setExpandedScope] = useState<string | null>(null);

  // Session state
  const [activeSession, setActiveSession] = useState<SessionInfo | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);

  // Query state
  const [query, setQuery] = useState('');
  const [queryParams, setQueryParams] = useState('');
  const [queryLimit, setQueryLimit] = useState(100);
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [queryHistory, setQueryHistory] = useState<Array<{ query: string; timestamp: string; rows: number }>>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingScopes, setLoadingScopes] = useState(true);
  const [countdown, setCountdown] = useState('');
  const [showSessions, setShowSessions] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [showAgentInfo, setShowAgentInfo] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load scopes on mount
  useEffect(() => {
    loadScopes();
    loadSessions();
  }, []);

  // Clean up copy timeouts on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  // Countdown timer for active session
  useEffect(() => {
    if (!activeSession || activeSession.status !== 'active') return;

    const tick = () => {
      const now = new Date();
      const expires = new Date(activeSession.expires_at);
      const diff = expires.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdown('EXPIRED');
        setActiveSession(prev => prev ? { ...prev, status: 'expired' } : null);
        return;
      }

      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown(`${mins}m ${secs}s`);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeSession]);

  const loadScopes = async () => {
    try {
      setLoadingScopes(true);
      const res = await adminFetch('/db/scopes');
      if (await handleAuthFailure(res)) return;
      if (res.ok) {
        const json = await res.json();
        setScopes(json.data);
      } else {
        const json = await res.json().catch(() => ({}));
        setError(json.detail || 'Failed to load database scopes.');
      }
    } catch (e) {
      console.error('Failed to load scopes:', e);
    } finally {
      setLoadingScopes(false);
    }
  };

  const loadSessions = async () => {
    try {
      const res = await adminFetch('/db/sessions');
      if (await handleAuthFailure(res)) return;
      if (res.ok) {
        const json = await res.json();
        setSessions(json.data || []);
        // If there's an active session, resume it
        const active = (json.data || []).find((s: SessionInfo) => s.status === 'active');
        if (active) {
          setActiveSession(active);
          setStage('query');
        }
      } else {
        const json = await res.json().catch(() => ({}));
        console.error('Failed to load sessions:', json.detail);
      }
    } catch (e) {
      console.error('Failed to load sessions:', e);
    }
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  const needsTargetUser = selectedScopes.some(s => s.startsWith('user_'));

  const createSession = async () => {
    if (!reason.trim() || reason.trim().length < 10) {
      setError('Reason must be at least 10 characters — document why you need access.');
      return;
    }
    if (selectedScopes.length === 0) {
      setError('Select at least one scope.');
      return;
    }
    if (needsTargetUser && !targetUserId.trim()) {
      setError('User data scopes require a target User ID.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await adminFetch('/db/access-request', {
        method: 'POST',
        body: JSON.stringify({
          reason: reason.trim(),
          scopes: selectedScopes,
          target_user_id: targetUserId.trim() || null,
          duration_minutes: duration,
        }),
      });

      if (await handleAuthFailure(res)) return;
      const json = await res.json();
      if (!res.ok) {
        setError(json.detail || 'Failed to create session');
        return;
      }

      const newSession: SessionInfo = {
        session_id: json.data.session_id,
        reason: reason.trim(),
        scopes: selectedScopes,
        target_user_id: targetUserId.trim() || null,
        created_at: new Date().toISOString(),
        expires_at: json.data.expires_at,
        duration_minutes: json.data.duration_minutes,
        query_count: 0,
        status: 'active',
        tables_accessible: json.data.tables_accessible,
      };

      setActiveSession(newSession);
      setStage('query');
      setQueryResult(null);
      setQueryHistory([]);
    } catch (e: any) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const closeSession = async () => {
    if (!activeSession) return;

    try {
      const res = await adminFetch('/db/close-session', {
        method: 'POST',
        body: JSON.stringify({ session_id: activeSession.session_id }),
      });
      if (!res.ok) {
        console.error('Failed to close session on server:', res.status);
      }
    } catch (e) {
      console.error('Failed to close session:', e);
    }

    setActiveSession(null);
    setStage('setup');
    setQueryResult(null);
    setQuery('');
    setSelectedScopes([]);
    setReason('');
    setTargetUserId('');
    loadSessions();
  };

  const executeQuery = async () => {
    if (!activeSession || !query.trim()) return;

    setLoading(true);
    setError(null);

    try {
      let params: any[] | undefined;
      if (queryParams.trim()) {
        try {
          params = JSON.parse(queryParams.trim());
          if (!Array.isArray(params)) {
            setError('Params must be a JSON array, e.g. ["value1", "value2"]');
            setLoading(false);
            return;
          }
        } catch {
          setError('Invalid JSON in params field. Use format: ["value1", 123]');
          setLoading(false);
          return;
        }
      }

      const res = await adminFetch('/db/query', {
        method: 'POST',
        body: JSON.stringify({
          session_id: activeSession.session_id,
          query: query.trim(),
          params: params || null,
          limit: queryLimit,
        }),
      });

      if (await handleAuthFailure(res)) return;
      const json = await res.json();

      if (res.status === 410) {
        // Session expired — trust the server
        setActiveSession(prev => prev ? { ...prev, status: 'expired' } : null);
        setError(json.detail || 'Access session has expired.');
        setLoading(false);
        return;
      }

      if (!res.ok) {
        setError(json.detail || 'Query failed');
        setLoading(false);
        return;
      }

      setQueryResult(json);
      setActiveSession(prev => prev ? { ...prev, query_count: prev.query_count + 1 } : null);
      setQueryHistory(prev => [
        { query: query.trim(), timestamp: new Date().toISOString(), rows: json.row_count },
        ...prev.slice(0, 19),
      ]);
    } catch (e: any) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      executeQuery();
    }
  };

  const setCopyFeedback = (type: string) => {
    setCopySuccess(type);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopySuccess(null), 2000);
  };

  const copyResultsAsCSV = async () => {
    if (!queryResult || !queryResult.data.length) return;
    const header = queryResult.columns.join(',');
    const rows = queryResult.data.map(row =>
      queryResult.columns.map(col => {
        const val = row[col];
        const str = val === null ? '' : String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    );
    try {
      await navigator.clipboard.writeText([header, ...rows].join('\n'));
      setCopyFeedback('csv');
    } catch (e) {
      console.error('Failed to copy CSV:', e);
    }
  };

  const copyResultsAsJSON = async () => {
    if (!queryResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(queryResult.data, null, 2));
      setCopyFeedback('json');
    } catch (e) {
      console.error('Failed to copy JSON:', e);
    }
  };

  // --- Render: Setup Stage ---
  const renderSetup = () => (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'linear-gradient(135deg, #1F6C4F, #42A5A5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: 20,
          }}>
            🔍
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0B1614', fontFamily: "'League Spartan', sans-serif" }}>
              Database Inspector
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
              Read-only access · Scoped · Time-limited · Fully audited
            </p>
          </div>
        </div>
      </div>

      {/* Active Sessions Banner */}
      {sessions.filter(s => s.status === 'active').length > 0 && (
        <div style={{
          padding: '12px 16px', marginBottom: 24, borderRadius: 8,
          backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#059669', fontWeight: 600, fontSize: 14 }}>Active session found</span>
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              — {sessions.find(s => s.status === 'active')?.reason}
            </span>
          </div>
          <button
            onClick={() => {
              const active = sessions.find(s => s.status === 'active');
              if (active) {
                setActiveSession(active);
                setStage('query');
              }
            }}
            style={{
              padding: '6px 16px', borderRadius: 6, border: 'none',
              backgroundColor: '#1F6C4F', color: 'white', fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Resume
          </button>
        </div>
      )}

      {/* Step 1: Reason */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#113C2B', marginBottom: 6 }}>
          1. Why do you need access?
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g., User X reported Garmin sync not showing HRV data since March 20. Consent received via support ticket #142."
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db',
            fontSize: 14, fontFamily: 'Inter, sans-serif', resize: 'vertical',
            minHeight: 72, outline: 'none', boxSizing: 'border-box',
          }}
          onFocus={e => e.target.style.borderColor = '#1F6C4F'}
          onBlur={e => e.target.style.borderColor = '#d1d5db'}
        />
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
          This is logged in the audit trail. Be specific about the issue and consent.
        </p>
      </div>

      {/* Step 2: Scopes */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <label style={{ fontSize: 14, fontWeight: 600, color: '#113C2B' }}>
            2. What data do you need?
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setSelectedScopes(Object.keys(scopes))}
              style={{ fontSize: 12, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Select all
            </button>
            <button
              onClick={() => setSelectedScopes([])}
              style={{ fontSize: 12, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Search */}
        <input
          type="text"
          value={scopeSearch}
          onChange={e => setScopeSearch(e.target.value)}
          placeholder="Search scopes or tables..."
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb',
            fontSize: 13, marginBottom: 12, outline: 'none', boxSizing: 'border-box',
          }}
        />

        {loadingScopes ? (
          <p style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: 20 }}>Loading scopes...</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(() => {
              const filtered = Object.entries(scopes)
                .filter(([name, info]) => {
                  if (!scopeSearch) return true;
                  const q = scopeSearch.toLowerCase();
                  return name.includes(q) || info.tables.some(t => t.includes(q)) ||
                    (SCOPE_META[name]?.label || '').toLowerCase().includes(q);
                });
              if (filtered.length === 0 && scopeSearch) {
                return (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 14 }}>
                    No scopes match "{scopeSearch}"
                  </div>
                );
              }
              return filtered.map(([name, info]) => {
                const meta = SCOPE_META[name] || { label: name, color: '#6b7280', icon: '📋' };
                const isSelected = selectedScopes.includes(name);
                const isExpanded = expandedScope === name;

                return (
                  <div key={name} style={{ gridColumn: isExpanded ? '1 / -1' : undefined }}>
                    <div
                      style={{
                        border: `2px solid ${isSelected ? meta.color : '#e5e7eb'}`,
                        borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
                        backgroundColor: isSelected ? `${meta.color}08` : 'white',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => toggleScope(name)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16 }}>{meta.icon}</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#113C2B' }}>{meta.label}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>
                              {info.table_count} tables · {info.requires_target_user ? 'requires user ID' : 'no user ID needed'}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            onClick={e => { e.stopPropagation(); setExpandedScope(isExpanded ? null : name); }}
                            style={{
                              background: 'none', border: 'none', color: '#9ca3af',
                              cursor: 'pointer', fontSize: 12, padding: '2px 6px',
                            }}
                          >
                            {isExpanded ? 'hide' : 'tables'}
                          </button>
                          <div style={{
                            width: 20, height: 20, borderRadius: 6,
                            border: `2px solid ${isSelected ? meta.color : '#d1d5db'}`,
                            backgroundColor: isSelected ? meta.color : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontSize: 14, fontWeight: 700,
                          }}>
                            {isSelected ? '✓' : ''}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{
                          marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb',
                          display: 'flex', flexWrap: 'wrap', gap: 4,
                        }}>
                          {info.tables.map(table => (
                            <span key={table} style={{
                              fontSize: 11, padding: '2px 8px', borderRadius: 4,
                              backgroundColor: '#f3f4f6', color: '#374151',
                              fontFamily: 'monospace',
                            }}>
                              {table}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* Step 3: Target User (conditional) */}
      {needsTargetUser && (
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#113C2B', marginBottom: 6 }}>
            3. Target User ID
          </label>
          <input
            type="text"
            value={targetUserId}
            onChange={e => setTargetUserId(e.target.value)}
            placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db',
              fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
            }}
          />
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
            Required for user data scopes. Queries are scoped to this user.
          </p>
        </div>
      )}

      {/* Step 4: Duration */}
      <div style={{ marginBottom: 32 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#113C2B', marginBottom: 6 }}>
          {needsTargetUser ? '4' : '3'}. Session Duration
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[15, 30, 60, 120].map(mins => (
            <button
              key={mins}
              onClick={() => setDuration(mins)}
              style={{
                padding: '8px 20px', borderRadius: 8, border: '2px solid',
                borderColor: duration === mins ? '#1F6C4F' : '#e5e7eb',
                backgroundColor: duration === mins ? '#E8F2ED' : 'white',
                color: duration === mins ? '#1F6C4F' : '#6b7280',
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              {mins < 60 ? `${mins}m` : `${mins / 60}h`}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, borderRadius: 8,
          backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
          color: '#DC2626', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Create Session Button */}
      <button
        onClick={createSession}
        disabled={loading || !reason.trim() || selectedScopes.length === 0}
        style={{
          width: '100%', padding: '14px 24px', borderRadius: 10, border: 'none',
          background: loading || !reason.trim() || selectedScopes.length === 0
            ? '#d1d5db'
            : 'linear-gradient(135deg, #1F6C4F, #42A5A5)',
          color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif', opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? 'Creating session...' : 'Open Access Session'}
      </button>

      {/* Session History */}
      {sessions.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <button
            onClick={() => setShowSessions(!showSessions)}
            style={{
              background: 'none', border: 'none', fontSize: 13, color: '#6b7280',
              cursor: 'pointer', textDecoration: 'underline', padding: 0,
            }}
          >
            {showSessions ? 'Hide' : 'Show'} session history ({sessions.length})
          </button>
          {showSessions && (
            <div style={{ marginTop: 12 }}>
              {sessions.map(s => (
                <div key={s.session_id} style={{
                  padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
                  marginBottom: 8, fontSize: 13,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11,
                        fontWeight: 600, marginRight: 8,
                        backgroundColor: s.status === 'active' ? '#ECFDF5' : s.status === 'expired' ? '#FEF3C7' : '#F3F4F6',
                        color: s.status === 'active' ? '#059669' : s.status === 'expired' ? '#D97706' : '#6b7280',
                      }}>
                        {s.status.toUpperCase()}
                      </span>
                      <span style={{ color: '#374151' }}>{s.reason}</span>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: 12 }}>
                      {s.query_count} queries · {new Date(s.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // --- Render: Query Stage ---
  const renderQueryStage = () => {
    if (!activeSession) return null;

    const isExpired = activeSession.status !== 'active';

    return (
      <div style={{ display: 'flex', gap: 24, height: 'calc(100vh - 180px)', minHeight: 500 }}>
        {/* Left: Query Editor */}
        <div style={{ flex: '0 0 45%', display: 'flex', flexDirection: 'column' }}>
          {/* Session bar */}
          <div style={{
            padding: '10px 16px', borderRadius: '10px 10px 0 0',
            background: isExpired
              ? 'linear-gradient(135deg, #6b7280, #9ca3af)'
              : 'linear-gradient(135deg, #1F6C4F, #42A5A5)',
            color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {isExpired ? 'Session Expired' : `Session Active · ${countdown}`}
              </div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>
                {activeSession.scopes.map(s => SCOPE_META[s]?.icon || '📋').join(' ')} · {activeSession.tables_accessible} tables · {activeSession.query_count} queries
              </div>
            </div>
            <button
              onClick={closeSession}
              style={{
                padding: '4px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.4)',
                backgroundColor: 'transparent', color: 'white', fontSize: 12,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              {isExpired ? 'New Session' : 'End Session'}
            </button>
          </div>

          {/* Reason + Agent Token */}
          <div style={{
            padding: '8px 16px', backgroundColor: '#f9fafb', borderLeft: '1px solid #e5e7eb',
            borderRight: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>Reason:</strong> {activeSession.reason}
                {activeSession.target_user_id && (
                  <span style={{ marginLeft: 12 }}>
                    <strong>User:</strong>{' '}
                    <code style={{ fontSize: 11, backgroundColor: '#e5e7eb', padding: '1px 6px', borderRadius: 3 }}>
                      {activeSession.target_user_id}
                    </code>
                  </span>
                )}
              </div>
              {!isExpired && (
                <button
                  onClick={() => setShowAgentInfo(!showAgentInfo)}
                  style={{
                    background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                    padding: '3px 10px', fontSize: 11, color: '#6b7280',
                    cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                  }}
                >
                  {showAgentInfo ? 'Hide' : 'AI Agent Token'}
                </button>
              )}
            </div>

            {/* Agent Token Panel */}
            {showAgentInfo && !isExpired && (
              <div style={{
                marginTop: 10, padding: 12, borderRadius: 8,
                backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 6 }}>
                  Agent Access Token
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <code style={{
                    flex: 1, fontSize: 11, padding: '6px 10px', borderRadius: 4,
                    backgroundColor: 'white', border: '1px solid #d1d5db',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontFamily: 'monospace', color: '#1e293b',
                  }}>
                    {activeSession.session_id}
                  </code>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(activeSession.session_id);
                        setTokenCopied(true);
                        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
                        copyTimeoutRef.current = setTimeout(() => setTokenCopied(false), 2000);
                      } catch (e) {
                        console.error('Failed to copy token:', e);
                      }
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: 'none',
                      backgroundColor: tokenCopied ? '#059669' : '#2563EB',
                      color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tokenCopied ? 'Copied!' : 'Copy Token'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                  <strong>Agent endpoint:</strong>{' '}
                  <code style={{ fontSize: 10, backgroundColor: 'white', padding: '1px 4px', borderRadius: 3 }}>
                    POST /api/admin/db/agent-query
                  </code>
                  <br />
                  <strong>Headers:</strong>{' '}
                  <code style={{ fontSize: 10, backgroundColor: 'white', padding: '1px 4px', borderRadius: 3 }}>
                    Authorization: Bearer {'<token>'}
                  </code>
                  <br />
                  <strong>Body:</strong>{' '}
                  <code style={{ fontSize: 10, backgroundColor: 'white', padding: '1px 4px', borderRadius: 3 }}>
                    {`{"query": "SELECT ...", "params": [...], "limit": 100}`}
                  </code>
                  <br />
                  <strong>Session info:</strong>{' '}
                  <code style={{ fontSize: 10, backgroundColor: 'white', padding: '1px 4px', borderRadius: 3 }}>
                    GET /api/admin/db/agent-session-info
                  </code>
                </div>
              </div>
            )}
          </div>

          {/* SQL Editor */}
          <textarea
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`SELECT * FROM unified_health_metrics\nWHERE user_id = $1\nORDER BY recorded_at DESC\nLIMIT 50`}
            disabled={isExpired}
            style={{
              flex: 1, padding: '14px 16px', border: '1px solid #e5e7eb',
              borderTop: 'none', fontSize: 13, fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
              lineHeight: 1.6, resize: 'none', outline: 'none', boxSizing: 'border-box',
              backgroundColor: isExpired ? '#f9fafb' : 'white', color: '#1e293b',
              minHeight: 180,
            }}
          />

          {/* Params + Limit Row */}
          <div style={{
            display: 'flex', gap: 8, padding: '8px 0',
          }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                value={queryParams}
                onChange={e => setQueryParams(e.target.value)}
                placeholder='Params (JSON array): ["uuid-here", 42]'
                disabled={isExpired}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6,
                  border: '1px solid #e5e7eb', fontSize: 12,
                  fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ width: 100 }}>
              <input
                type="number"
                value={queryLimit}
                onChange={e => setQueryLimit(Math.min(1000, Math.max(1, parseInt(e.target.value) || 100)))}
                disabled={isExpired}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6,
                  border: '1px solid #e5e7eb', fontSize: 12, outline: 'none', boxSizing: 'border-box',
                }}
                min={1}
                max={1000}
              />
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2, textAlign: 'center' }}>max rows</div>
            </div>
          </div>

          {/* Execute Button */}
          <button
            onClick={executeQuery}
            disabled={loading || isExpired || !query.trim()}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              backgroundColor: loading || isExpired || !query.trim() ? '#d1d5db' : '#1F6C4F',
              color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {loading ? 'Executing...' : 'Run Query'}{!isExpired && ' (⌘+Enter)'}
          </button>

          {/* Error */}
          {error && (
            <div style={{
              padding: '10px 14px', marginTop: 8, borderRadius: 8,
              backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
              color: '#DC2626', fontSize: 13, wordBreak: 'break-word',
            }}>
              {error}
            </div>
          )}

          {/* Query History */}
          {queryHistory.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Recent Queries</div>
              <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                {queryHistory.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => setQuery(h.query)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '4px 8px', borderRadius: 4, border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: 11, fontFamily: 'monospace', color: '#374151',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                    onMouseEnter={e => (e.target as HTMLElement).style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={e => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                  >
                    <span style={{ color: '#9ca3af' }}>{h.rows}r</span> {h.query}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Results */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {queryResult ? (
            <>
              {/* Results header */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: '1px solid #e5e7eb',
              }}>
                <div style={{ fontSize: 13, color: '#374151' }}>
                  <strong>{queryResult.row_count}</strong> rows · <strong>{queryResult.query_time_ms}ms</strong>
                  {queryResult.columns.length > 0 && (
                    <span style={{ color: '#9ca3af' }}> · {queryResult.columns.length} columns</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={copyResultsAsCSV}
                    style={{
                      padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                      backgroundColor: copySuccess === 'csv' ? '#ECFDF5' : 'white',
                      fontSize: 12, cursor: 'pointer',
                      color: copySuccess === 'csv' ? '#059669' : '#374151',
                    }}
                  >
                    {copySuccess === 'csv' ? 'Copied!' : 'Copy CSV'}
                  </button>
                  <button
                    onClick={copyResultsAsJSON}
                    style={{
                      padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                      backgroundColor: copySuccess === 'json' ? '#ECFDF5' : 'white',
                      fontSize: 12, cursor: 'pointer',
                      color: copySuccess === 'json' ? '#059669' : '#374151',
                    }}
                  >
                    {copySuccess === 'json' ? 'Copied!' : 'Copy JSON'}
                  </button>
                </div>
              </div>

              {/* Results table */}
              <div style={{ flex: 1, overflow: 'auto', marginTop: 8 }}>
                {queryResult.data.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>
                    No rows returned
                  </div>
                ) : (
                  <table style={{
                    width: '100%', borderCollapse: 'collapse', fontSize: 12,
                    fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                  }}>
                    <thead>
                      <tr>
                        {queryResult.columns.map(col => (
                          <th key={col} style={{
                            padding: '8px 12px', textAlign: 'left', fontWeight: 600,
                            borderBottom: '2px solid #e5e7eb', color: '#374151',
                            whiteSpace: 'nowrap', position: 'sticky', top: 0,
                            backgroundColor: 'white', fontSize: 11,
                          }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {queryResult.data.map((row, i) => (
                        <tr
                          key={i}
                          style={{ backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb' }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EFF6FF')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? 'white' : '#f9fafb')}
                        >
                          {queryResult.columns.map(col => {
                            const val = row[col];
                            const display = val === null
                              ? 'NULL'
                              : typeof val === 'object'
                                ? JSON.stringify(val)
                                : String(val);
                            const isNull = val === null;

                            return (
                              <td key={col} style={{
                                padding: '6px 12px', borderBottom: '1px solid #f3f4f6',
                                maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap', color: isNull ? '#d1d5db' : '#1e293b',
                                fontStyle: isNull ? 'italic' : 'normal',
                              }}
                              title={display}
                              >
                                {display.length > 80 ? display.substring(0, 80) + '...' : display}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#9ca3af', fontSize: 14, flexDirection: 'column', gap: 8,
            }}>
              <div style={{ fontSize: 32 }}>🔍</div>
              <div>Run a query to see results</div>
              <div style={{ fontSize: 12 }}>
                Available tables: {activeSession.tables_accessible} across {activeSession.scopes.length} scopes
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: stage === 'query' ? 0 : undefined }}>
      {stage === 'setup' && renderSetup()}
      {stage === 'query' && renderQueryStage()}
    </div>
  );
};
